/**
 * Developed by Ashvek Technologies · https://ashvek.com · Evidence over prompts
 */

/**
 * Three.js WebGL Scene Engine for Ashvek Technologies
 * Renders the precision geometric 'A' emblem with parallax gyro and reduced-motion fallback.
 */

import * as THREE from "three";
import { vertexShader, fragmentShader } from "./shaders";

export interface SceneOptions {
  canvas: HTMLCanvasElement;
  enableMouseParallax?: boolean;
  onContextLost?: () => void;
  onContextRestored?: () => void;
  onError?: (err: Error) => void;
}

export class AshvekScene {
  private canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private mesh: THREE.Mesh | null = null;
  private material: THREE.ShaderMaterial | null = null;
  private wireframe: THREE.LineSegments | null = null;
  private animationFrameId: number | null = null;
  private targetRotationX = 0;
  private targetRotationY = 0;
  private mouseHandler: ((e: MouseEvent) => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private contextLostHandler: ((e: Event) => void) | null = null;
  private contextRestoredHandler: (() => void) | null = null;
  private startTime = performance.now();
  private options: SceneOptions;

  constructor(options: SceneOptions) {
    this.options = options;
    const { canvas, enableMouseParallax = true } = options;
    this.canvas = canvas;

    // 1. WebGL 2.0 Device Capability Detection
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!gl) {
      const err = new Error("WebGL 2.0 is not supported on this device/browser");
      if (options.onError) options.onError(err);
      throw err;
    }

    // 2. WebGL Renderer with High-Performance Profile
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas,
        context: gl as WebGLRenderingContext,
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
      this.renderer.setPixelRatio(Math.min(typeof window !== "undefined" ? window.devicePixelRatio : 1, 2));
      this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    } catch (e: any) {
      const err = new Error("Renderer construction failed: " + (e ? e.message : "unknown"));
      if (options.onError) options.onError(err);
      throw err;
    }

    // 3. Scene & Camera Setup
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      45,
      canvas.clientWidth / Math.max(canvas.clientHeight, 1),
      0.1,
      100
    );
    this.camera.position.set(0, 0, 5);

    // 4. Build Precision Geometry & Shaders
    this.buildGeometry();

    // 5. Parallax Gyro Listener
    if (enableMouseParallax && typeof window !== "undefined") {
      this.mouseHandler = (e: MouseEvent) => {
        const x = (e.clientX / window.innerWidth) * 2 - 1;
        const y = -(e.clientY / window.innerHeight) * 2 + 1;
        this.targetRotationY = x * 0.35;
        this.targetRotationX = -y * 0.25;
      };
      window.addEventListener("mousemove", this.mouseHandler, { passive: true });
    }

    // 6. Responsive Resize Listener
    if (typeof window !== "undefined") {
      this.resizeHandler = () => this.handleResize(canvas);
      window.addEventListener("resize", this.resizeHandler, { passive: true });
    }

    // 7. Context Loss & Restoration Lifecycle Handlers
    this.contextLostHandler = (e: Event) => {
      e.preventDefault();
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      if (this.options.onContextLost) this.options.onContextLost();
    };

    this.contextRestoredHandler = () => {
      this.buildGeometry();
      this.render();
      if (this.options.onContextRestored) this.options.onContextRestored();
    };

    canvas.addEventListener("webglcontextlost", this.contextLostHandler, false);
    canvas.addEventListener("webglcontextrestored", this.contextRestoredHandler, false);

    // 8. Start Render Loop
    this.render();
  }

  private buildGeometry() {
    // Sharp octahedron prism symbolizing the Ashvek 'A' apex
    const geometry = new THREE.OctahedronGeometry(1.3, 0);

    // Custom obsidian shader material with Mars-red (#FF3B30) rim
    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uBaseColor: { value: new THREE.Color("#090D16") },
        uRimColor: { value: new THREE.Color("#FF3B30") },
        uRimPower: { value: 2.8 },
        uTime: { value: 0 },
      },
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    // Outer wireframe overlay for precision engineering aesthetic
    const wireframeGeometry = new THREE.WireframeGeometry(geometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: 0xFF3B30,
      transparent: true,
      opacity: 0.35,
    });
    this.wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
    this.mesh.add(this.wireframe);
  }

  private render = () => {
    const elapsed = (performance.now() - this.startTime) * 0.001;

    if (this.material) {
      this.material.uniforms.uTime.value = elapsed;
    }

    if (this.mesh) {
      // Continuous subtle precession
      this.mesh.rotation.y += (this.targetRotationY + elapsed * 0.25 - this.mesh.rotation.y) * 0.05;
      this.mesh.rotation.x += (this.targetRotationX + Math.sin(elapsed * 0.5) * 0.1 - this.mesh.rotation.x) * 0.05;
    }

    this.renderer.render(this.scene, this.camera);
    this.animationFrameId = requestAnimationFrame(this.render);
  };

  private handleResize(canvas: HTMLCanvasElement) {
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  public dispose() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.mouseHandler && typeof window !== "undefined") {
      window.removeEventListener("mousemove", this.mouseHandler);
    }
    if (this.resizeHandler && typeof window !== "undefined") {
      window.removeEventListener("resize", this.resizeHandler);
    }
    if (this.canvas) {
      if (this.contextLostHandler) {
        this.canvas.removeEventListener("webglcontextlost", this.contextLostHandler);
      }
      if (this.contextRestoredHandler) {
        this.canvas.removeEventListener("webglcontextrestored", this.contextRestoredHandler);
      }
    }
    if (this.wireframe) {
      this.wireframe.geometry.dispose();
      (this.wireframe.material as THREE.Material).dispose();
    }
    if (this.mesh) {
      this.mesh.geometry.dispose();
      if (this.material) this.material.dispose();
    }
    this.renderer.dispose();
  }
}
